import React, { useState, useEffect } from 'react';
import './PolicyLayout.scss';

interface PolicySection {
  id: string;
  title: string;
}

interface PolicyLayoutProps {
  title: string;
  sections: PolicySection[];
  children: React.ReactNode;
}

// Declare jQuery on window for TypeScript
declare global {
  interface Window {
    jQuery?: any;
  }
}

const PolicyLayout: React.FC<PolicyLayoutProps> = ({ title, sections, children }) => {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id || '');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY; // Adjust based on header height
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    // Add passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Use consistent offset with CSS scroll-margin-top
      const offsetTop = element.offsetTop; // Account for sticky header
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="policy-layout">
      <div className="container">
        <div className="row">
          {/* Navigation Sidebar - Desktop/Tablet only */}
          <div className="col-lg-3 col-md-4 sticky-sidebar d-none d-md-block">
            <div className="policy-navigation">
              <h4>{title}</h4>
              <ul className="policy-nav-list">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      className={`policy-nav-link ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Content Area */}
          <div className="col-lg-9 col-md-8">
            <div className="policy-content">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyLayout; 